import { ReactNode } from 'react'
import { Stack, Text, Tooltip } from '@common/ui'
import { Anchor } from '@common/ui/mantine'
import { Link as RouterLink } from 'react-router-dom'
import { com } from '@eidu/web'
import MergeEditor from '../../components/data/MergeEditor'
import useEntityType from '../../io/useEntityType'
import { AuditEntry, Change } from '../../io/api/audit/data/AuditEntry'
import classes from './changeRendering.module.css'
import entityTypeIdFromStringOrNull = com.eidu.entity.type.entityTypeIdFromStringOrNull
import EntityTypeId = com.eidu.entity.type.EntityTypeId

type Json = unknown

const isPlainObject = (v: Json): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v)

const parseValue = (v: string | null): Json => {
  if (v === null) return null
  try {
    return JSON.parse(v)
  } catch {
    return v
  }
}

const formatScalar = (v: Json): string => {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

const formatFieldValue = (v: Json): string => {
  if (isPlainObject(v) && typeof v.type === 'string' && 'value' in v) return formatScalar(v.value)
  return formatScalar(v)
}

const permissionLabel = (p: Record<string, Json>): string => {
  const action = String(p.action ?? '')
  if (p.type === 'Global') return action
  const targets = isPlainObject(p.targets) ? p.targets : null
  const targetSummary = targets?.type === 'All' ? '*' : targets?.type ?? '?'
  return `${action}@${targetSummary}`
}

const samePermission = (a: Record<string, Json>, b: Record<string, Json>) => JSON.stringify(a) === JSON.stringify(b)

const diffPermissionLists = (
  prev: Record<string, Json>[],
  next: Record<string, Json>[]
): { added: string[]; removed: string[] } => {
  const added = next.filter((n) => !prev.some((p) => samePermission(p, n))).map(permissionLabel)
  const removed = prev.filter((p) => !next.some((n) => samePermission(n, p))).map(permissionLabel)
  return { added, removed }
}

const summarizePermissionUpdate = (oldValue: Json, newValue: Json): ReactNode => {
  if (!isPlainObject(oldValue) || !isPlainObject(newValue)) return null
  const oldPerms = (oldValue.permissions as Record<string, Json>[]) ?? []
  const newPerms = (newValue.permissions as Record<string, Json>[]) ?? []
  const { added, removed } = diffPermissionLists(oldPerms, newPerms)
  const selectorChanged = JSON.stringify(oldValue.userSelector) !== JSON.stringify(newValue.userSelector)
  if (added.length === 0 && removed.length === 0 && !selectorChanged) return <em>no permission changes</em>
  return (
    <Stack gap="xxs">
      {added.length > 0 && (
        <Text size="sm" c="positive">
          + {added.join(', ')}
        </Text>
      )}
      {removed.length > 0 && (
        <Text size="sm" c="negative">
          − {removed.join(', ')}
        </Text>
      )}
      {selectorChanged && <Text size="sm">user selector changed</Text>}
    </Stack>
  )
}

const summarizePermissionGrant = (newValue: Json): ReactNode => {
  if (!isPlainObject(newValue)) return null
  const perms = (newValue.permissions as Record<string, Json>[]) ?? []
  return (
    <Text size="sm" c="positive">
      + {perms.map(permissionLabel).join(', ')}
    </Text>
  )
}

const summarizePermissionRevoke = (oldValue: Json): ReactNode => {
  if (!isPlainObject(oldValue)) return null
  const perms = (oldValue.permissions as Record<string, Json>[]) ?? []
  return (
    <Text size="sm" c="negative">
      − {perms.map(permissionLabel).join(', ')}
    </Text>
  )
}

const ResolvedFieldName = ({ typeId, fieldId, target }: { typeId: EntityTypeId; fieldId: string; target: string }) => {
  const { entityType } = useEntityType(typeId)
  const field = entityType?.fields.asJsReadonlyArrayView().find((f) => f.id.toString() === fieldId)
  const name = field?.name ?? null
  if (!name) return <>{target}</>
  return (
    <Tooltip label={target} position="top">
      <span>{name}</span>
    </Tooltip>
  )
}

const ChangeTargetName = ({ entry, target }: { entry: AuditEntry; target: string }) => {
  const isTypeField = entry.action === 'EntityTypeFieldUpdate'
  const isFieldOnEntity = entry.action === 'EntityValueUpdate'
  const lookupTypeIdString = isTypeField ? entry.targetId : isFieldOnEntity ? entry.targetTypeId : null
  const typeId = lookupTypeIdString ? entityTypeIdFromStringOrNull(lookupTypeIdString) : null
  if (!typeId) return <>{target}</>
  return <ResolvedFieldName typeId={typeId} fieldId={target} target={target} />
}

const summarizeFieldValueChange = (entry: AuditEntry, changes: Change[]): ReactNode => {
  const isEntityValue = entry.action === 'EntityValueUpdate'
  const formatValue = isEntityValue ? formatFieldValue : formatScalar
  return (
    <Stack gap="xxs">
      {changes.map((c, idx) => (
        <Text key={idx} size="sm">
          {c.target && (
            <>
              <span style={{ color: 'var(--trellis-color-text-secondary)' }}>
                <ChangeTargetName entry={entry} target={c.target} />:
              </span>{' '}
            </>
          )}
          <span style={{ fontFamily: 'var(--trellis-font-mono)' }}>
            {formatValue(parseValue(c.oldValue))} → {formatValue(parseValue(c.newValue))}
          </span>
        </Text>
      ))}
    </Stack>
  )
}

const summarizeWholeRecord = (newValue: Json, label: string): ReactNode => {
  if (isPlainObject(newValue) && typeof newValue.name === 'string')
    return (
      <Text size="sm">
        {label}: {newValue.name}
      </Text>
    )
  return <Text size="sm">{label}</Text>
}

const summarizeInvite = (newValue: Json): ReactNode => {
  if (!isPlainObject(newValue)) return null
  const recipient = newValue.email ?? newValue.entityId
  return <Text size="sm">invited: {String(recipient)}</Text>
}

const summarizePublish = (entry: AuditEntry, newValue: Json): ReactNode => {
  if (!isPlainObject(newValue)) return null
  const versionId = newValue.versionId ? String(newValue.versionId) : ''
  if (!versionId) return null
  const canLink = entry.category === 'Entity' && !!entry.targetTypeId
  const versionText = <span style={{ fontFamily: 'var(--trellis-font-mono)' }}>{versionId}</span>
  const versionNode = canLink ? (
    <Anchor
      component={RouterLink}
      to={`/entities/${entry.targetTypeId}/view/${entry.targetId}?versionHistory=1&compareVersion=${versionId}`}
      underline="hover"
      c="inherit"
      inherit
    >
      {versionText}
    </Anchor>
  ) : (
    versionText
  )
  return <Text size="sm">version: {versionNode}</Text>
}

export const renderChangeSummary = (entry: AuditEntry): ReactNode => {
  const { action, changes } = entry
  if (!changes || changes.length === 0) {
    if (action === 'EntityRecover' || action === 'EntityTypeRecover') return <em>recovered</em>
    if (action === 'EntityDelete' || action === 'EntityTypeDelete') return <em>deleted</em>
    return null
  }
  const single = changes.length === 1 ? changes[0] : null

  switch (action) {
    case 'PermissionUpdate':
      return single ? summarizePermissionUpdate(parseValue(single.oldValue), parseValue(single.newValue)) : null
    case 'PermissionGrant':
      return single ? summarizePermissionGrant(parseValue(single.newValue)) : null
    case 'PermissionRevoke':
      return single ? summarizePermissionRevoke(parseValue(single.oldValue)) : null

    case 'EntityValueUpdate':
    case 'EntityLinkUpdate':
    case 'EntityTypeFieldUpdate':
      return summarizeFieldValueChange(entry, changes)

    case 'EntityCreate':
      return single ? summarizeWholeRecord(parseValue(single.newValue), 'created') : null
    case 'EntityTypeCreate':
      return single ? summarizeWholeRecord(parseValue(single.newValue), 'created type') : null

    case 'EntityTypeNameUpdate':
      return single ? (
        <Text size="sm">
          name: {formatScalar(parseValue(single.oldValue))} → {formatScalar(parseValue(single.newValue))}
        </Text>
      ) : null
    case 'EntityTypeKindUpdate':
      return single ? (
        <Text size="sm">
          kind: {formatScalar(parseValue(single.oldValue))} → {formatScalar(parseValue(single.newValue))}
        </Text>
      ) : null
    case 'EntityTypeLabelUpdate':
      return single ? <Text size="sm">label updated</Text> : null

    case 'EntityPublish':
      return single ? summarizePublish(entry, parseValue(single.newValue)) : null
    case 'EntityInviteUser':
      return single ? summarizeInvite(parseValue(single.newValue)) : null

    case 'SemanticLinkCreate':
      return summarizeWholeRecord(parseValue(single?.newValue ?? null), 'created link')
    case 'SemanticLinkDelete':
      return <em>deleted</em>

    default:
      return null
  }
}

const prettyJson = (raw: string | null): string => {
  if (raw === null) return ''
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

const ChangeDiff = ({ entry, change }: { entry: AuditEntry; change: Change }) => (
  <Stack gap="xxs">
    {change.target && (
      <Text size="xs" c="dimmed">
        <ChangeTargetName entry={entry} target={change.target} />
      </Text>
    )}
    <div className={classes.diffContainer}>
      <MergeEditor left={prettyJson(change.oldValue)} right={prettyJson(change.newValue)} />
    </div>
  </Stack>
)

export const renderChangeDetail = (entry: AuditEntry): ReactNode => {
  if (!entry.changes || entry.changes.length === 0) return null
  return (
    <Stack gap="sm">
      {entry.changes.map((c, idx) => (
        <ChangeDiff key={idx} entry={entry} change={c} />
      ))}
    </Stack>
  )
}
